import featureConfig from "../../../feature-config.json";
import { UserRole } from "../interfaces/sidemenu.interface";

type FeatureValue =
  | boolean
  | {
      enabled: boolean;
      roles?: UserRole[];
    };

export type Feature = {
  __module?: FeatureValue;
  [featureName: string]: FeatureValue | undefined;
};

export type FeatureConfig = {
  [ModuleName in keyof typeof featureConfig]: Feature;
};

const FEATURE_DISABLED: FeatureValue = false;

class FeatureConfigManager {
  private config: FeatureConfig;

  constructor(config: FeatureConfig) {
    this.config = Object.fromEntries(
      Object.entries(config).map(([module, features]) => [
        module,
        { ...features },
      ]),
    ) as FeatureConfig;
  }

  private safeParseFeatureValue(value: FeatureValue | undefined): FeatureValue {
    if (value === null || value === undefined) {
      return FEATURE_DISABLED;
    }

    if (typeof value === "boolean") {
      return value;
    }

    if (typeof value === "object" && !Array.isArray(value)) {
      const obj = value as Record<string, unknown>;

      if (typeof obj["enabled"] !== "boolean") {
        return FEATURE_DISABLED;
      }

      if (!obj["enabled"]) {
        return FEATURE_DISABLED;
      }

      if (obj["roles"] !== undefined && !Array.isArray(obj["roles"])) {
        return FEATURE_DISABLED;
      }

      return {
        enabled: true,
        ...(Array.isArray(obj["roles"]) && {
          roles: obj["roles"] as UserRole[],
        }),
      };
    }

    return FEATURE_DISABLED;
  }

  private isAllowedByRoles(
    roles: UserRole[] | undefined,
    role?: UserRole,
  ): boolean {
    if (!roles || roles.length === 0 || !role) return true;
    return roles.includes(role);
  }

  isFeatureEnabled(
    moduleName: keyof FeatureConfig,
    featureName: string,
    role?: UserRole,
  ): boolean {
    const feature = this.safeParseFeatureValue(
      this.config[moduleName]?.[featureName],
    );

    if (typeof feature === "boolean") return feature;

    if (!feature.enabled) return false;

    return this.isAllowedByRoles(feature.roles, role);
  }

  isModuleEnabled(moduleName: keyof FeatureConfig, role?: UserRole): boolean {
    const moduleConfig = this.config[moduleName];

    if (!moduleConfig) return false;

    const rawValue = moduleConfig["__module"];

    if (rawValue === undefined || rawValue === null) return true;

    const moduleFeature = this.safeParseFeatureValue(rawValue);

    if (typeof moduleFeature === "boolean") return moduleFeature;

    if (!moduleFeature.enabled) return false;

    return this.isAllowedByRoles(moduleFeature.roles, role);
  }

  toggleFeature(moduleName: keyof FeatureConfig, featureName: string) {
    if (!this.config[moduleName]) {
      this.config[moduleName] = {};
    }

    const featureValue = this.safeParseFeatureValue(
      this.config[moduleName]?.[featureName],
    );

    if (typeof featureValue === "boolean") {
      this.config[moduleName][featureName] = !featureValue;
    } else {
      this.config[moduleName][featureName] = {
        ...featureValue,
        enabled: !featureValue.enabled,
      };
    }
  }

  getConfig(): FeatureConfig {
    return this.config;
  }
}

const featureConfigManager = new FeatureConfigManager(featureConfig);

export default featureConfigManager;
